import { getGameSettings } from "@/game/model/game-settings";
import type { HomeEconomy, HomePurchaseRecord, HomeWallet } from "@/home/economy";
import {
  HOME_STRUCTURE_DEF_CARD_SKU,
  HOME_STRUCTURE_HP_CARD_SKU,
  HOME_STRUCTURE_HP_POTION_SKU,
  HOME_STRUCTURE_REGEN_CARD_SKU,
} from "@/home/home-structure-types";
import {
  clampStructureCardApplications,
  homeStructureCardsFromEconomy,
  homeStructureStatConfigFromSettings,
} from "@/home/home-structure-stats";

function newPurchaseId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function homeStructureHpPotions(economy: HomeEconomy): number {
  return Math.max(0, Math.floor(economy.wallet.homeStructureHpPotions ?? 0));
}

export function homeStructureHpCards(economy: HomeEconomy): number {
  return Math.max(0, Math.floor(economy.homeStructureHpCards ?? 0));
}

export function homeStructureDefCards(economy: HomeEconomy): number {
  return Math.max(0, Math.floor(economy.homeStructureDefCards ?? 0));
}

export function homeStructureRegenCards(economy: HomeEconomy): number {
  return Math.max(0, Math.floor(economy.homeStructureRegenCards ?? 0));
}

type Ok<T> = { ok: true } & T;
type Fail = { ok: false; reason: string };

export function buyHomeStructureHpCard(economy: HomeEconomy): Ok<{ economy: HomeEconomy }> | Fail {
  const cost = getGameSettings().homeStructureHpCardCashCost;
  if (economy.wallet.cash < cost) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${cost}` };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: HOME_STRUCTURE_HP_CARD_SKU,
    kind: "home_structure_hp_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: cost,
    provider: "mock",
  };
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: { ...economy.wallet, cash: economy.wallet.cash - cost },
      homeStructureHpCards: homeStructureHpCards(economy) + 1,
      purchases: [record, ...economy.purchases].slice(0, 50),
    },
  };
}

export function buyHomeStructureDefCard(economy: HomeEconomy): Ok<{ economy: HomeEconomy }> | Fail {
  const cost = getGameSettings().homeStructureDefCardCashCost;
  if (economy.wallet.cash < cost) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${cost}` };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: HOME_STRUCTURE_DEF_CARD_SKU,
    kind: "home_structure_def_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: cost,
    provider: "mock",
  };
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: { ...economy.wallet, cash: economy.wallet.cash - cost },
      homeStructureDefCards: homeStructureDefCards(economy) + 1,
      purchases: [record, ...economy.purchases].slice(0, 50),
    },
  };
}

export function buyHomeStructureRegenCard(
  economy: HomeEconomy,
): Ok<{ economy: HomeEconomy }> | Fail {
  const cost = getGameSettings().homeStructureRegenCardCashCost;
  if (economy.wallet.cash < cost) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${cost}` };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: HOME_STRUCTURE_REGEN_CARD_SKU,
    kind: "home_structure_regen_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: cost,
    provider: "mock",
  };
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: { ...economy.wallet, cash: economy.wallet.cash - cost },
      homeStructureRegenCards: homeStructureRegenCards(economy) + 1,
      purchases: [record, ...economy.purchases].slice(0, 50),
    },
  };
}

export function buyHomeStructureHpPotion(economy: HomeEconomy): Ok<{ economy: HomeEconomy }> | Fail {
  const cost = getGameSettings().homeStructurePotionCoinPrice;
  if (economy.wallet.coins < cost) {
    return {
      ok: false,
      reason: `ทองไม่พอ · ต้องการ ${cost.toLocaleString("en-US")}`,
    };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: HOME_STRUCTURE_HP_POTION_SKU,
    kind: "home_structure_hp_potion",
    status: "mock_granted",
    createdAt: Date.now(),
    provider: "mock",
  };
  const wallet: HomeWallet = {
    ...economy.wallet,
    coins: economy.wallet.coins - cost,
    homeStructureHpPotions: homeStructureHpPotions(economy) + 1,
  };
  return {
    ok: true,
    economy: {
      ...economy,
      wallet,
      purchases: [record, ...economy.purchases].slice(0, 50),
    },
  };
}

export function applyHomeStructureHpCard(economy: HomeEconomy): Ok<{ economy: HomeEconomy }> | Fail {
  if (homeStructureHpCards(economy) <= 0) {
    return { ok: false, reason: "ไม่มีบัตร HP บ้าน — ซื้อที่ร้าน Cash" };
  }
  const config = homeStructureStatConfigFromSettings();
  const applied = homeStructureCardsFromEconomy(economy);
  if (applied.hpCards >= config.homeStructureHpCardMax) {
    return { ok: false, reason: `อัพ HP บ้านถึงเพดานแล้ว (${config.homeStructureHpCardMax} ใบ)` };
  }
  return {
    ok: true,
    economy: {
      ...economy,
      homeStructureHpCards: homeStructureHpCards(economy) - 1,
      homeStructureHpCardsApplied: applied.hpCards + 1,
    },
  };
}

export function applyHomeStructureDefCard(economy: HomeEconomy): Ok<{ economy: HomeEconomy }> | Fail {
  if (homeStructureDefCards(economy) <= 0) {
    return { ok: false, reason: "ไม่มีบัตรป้องกันบ้าน — ซื้อที่ร้าน Cash" };
  }
  const config = homeStructureStatConfigFromSettings();
  const applied = homeStructureCardsFromEconomy(economy);
  if (applied.defCards >= config.homeStructureDefCardMax) {
    return { ok: false, reason: `อัพป้องกันบ้านถึงเพดานแล้ว (${config.homeStructureDefCardMax} ใบ)` };
  }
  return {
    ok: true,
    economy: {
      ...economy,
      homeStructureDefCards: homeStructureDefCards(economy) - 1,
      homeStructureDefCardsApplied: applied.defCards + 1,
    },
  };
}

export function applyHomeStructureRegenCard(
  economy: HomeEconomy,
): Ok<{ economy: HomeEconomy }> | Fail {
  if (homeStructureRegenCards(economy) <= 0) {
    return { ok: false, reason: "ไม่มีบัตรฟื้นบ้าน — ซื้อที่ร้าน Cash" };
  }
  const config = homeStructureStatConfigFromSettings();
  const applied = homeStructureCardsFromEconomy(economy);
  if (applied.regenCards >= config.homeStructureRegenCardMax) {
    return {
      ok: false,
      reason: `อัพฟื้นบ้านถึงเพดานแล้ว (${config.homeStructureRegenCardMax} ใบ)`,
    };
  }
  return {
    ok: true,
    economy: {
      ...economy,
      homeStructureRegenCards: homeStructureRegenCards(economy) - 1,
      homeStructureRegenCardsApplied: applied.regenCards + 1,
    },
  };
}

export function normalizeHomeStructureAutoHpPercent(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 50;
  return Math.min(99, Math.max(1, Math.floor(value)));
}

export function setHomeStructureAutoPotionEnabled(
  economy: HomeEconomy,
  enabled: boolean,
): HomeEconomy {
  if (economy.homeStructureAutoPotionEnabled === enabled) return economy;
  return { ...economy, homeStructureAutoPotionEnabled: enabled };
}

export function setHomeStructureAutoPotionHpPercent(
  economy: HomeEconomy,
  percent: number,
): HomeEconomy {
  const next = normalizeHomeStructureAutoHpPercent(percent);
  if (economy.homeStructureAutoPotionHpPercent === next) return economy;
  return { ...economy, homeStructureAutoPotionHpPercent: next };
}

/** Economy slice for server auto-potion + publish snapshot. */
export function homeStructureEconomySnapshot(economy: HomeEconomy) {
  const config = homeStructureStatConfigFromSettings();
  const cards = clampStructureCardApplications(homeStructureCardsFromEconomy(economy), config);
  return {
    cards,
    potions: homeStructureHpPotions(economy),
    autoEnabled: economy.homeStructureAutoPotionEnabled !== false,
    autoHpPercent: normalizeHomeStructureAutoHpPercent(economy.homeStructureAutoPotionHpPercent),
  };
}
